/**
 * **Per-hierarchy breakdowns** for the Analytics & Insights panel — client directive,
 * 9 Sep 2026, item 3.
 *
 * > *"Rich, filter-responsive insights for BOTH baseline variables and (contractual /
 * > thematic) indicators … a pie/donut of composition or share by country (or by watershed
 * > under a country filter), a bar breakdown across the operational hierarchy level below the
 * > current filter, the key stat + sample-n as today, and the temporal spark where a real
 * > series exists."*
 *
 * This module is the *shaping*, with no React and no fetching in it, because the two families
 * the directive names have to be answered by one design and the difference between them is
 * entirely a question about data:
 *
 * * **survey estimates** (`aggregation: 'none'` — the 2025 MELS baseline) are stored at the
 *   strata they were measured at and nowhere else. Their breakdown may only ever ask for a
 *   level `effectiveLevels` admits, and where that leaves nothing the block is **dropped**.
 *   Inventing a region breakdown by dividing a country estimate is precisely the roll-up
 *   §E4 refuses to do for them, and a chart is not a licence to do it in the browser.
 * * **roll-up indicators** (`sum` / `mean`) have the whole ladder, so every rung below the
 *   filter has an answer.
 *
 * One function decides the rung (`breakdownPlan`), one shapes the rows (`breakdownRows`) and
 * one decides whether a donut would be telling the truth (`donutShape`). All three are pure,
 * so the `{0, 1, n}` unit counts the directive asks about are unit tests rather than screen
 * inspections.
 *
 * ## Composing existing calls, not a new endpoint
 *
 * Everything below is served by two calls the explorer already makes:
 * `/indicators/{id}/values?level=&parent=&country=` for the numbers (with `sample_n` on the
 * rows already) and `/hierarchy/options?type=&level=&parent=` for the names — the 4.9 kB
 * name list the §F5.8 cascade fetches, **not** the polygons. Under a country filter the
 * cascade is holding the same `options` entry this asks for, so the bars usually cost one
 * request and often none.
 */
import type { UnitOption, UnitType } from '../../lib/api';
import {
  ALL_REPORTING_LEVELS,
  effectiveLevels,
  PARTNERSHIP_LEVEL,
  WATERSHED_LEVEL,
  type Country,
  type LevelCapableIndicator,
  type ReportingLevel,
  type Scope,
} from '../../lib/state';

/** Where the reader currently is in the §F5.8 cascade — the rung the breakdown is *below*. */
export type Rung = 'programme' | 'country' | 'region' | 'commune' | 'watershed' | 'partnership';

/**
 * The reader's rung, from the two fields the cascade actually writes.
 *
 * A country is **not** a `Scope` (§F1 owns that control — see `Scope`), so the country rung
 * is `country !== 'ALL'` with no scope, and a hand-edited `sc=BF` hash that does produce an
 * admin-0 scope lands on the same rung rather than on a sixth case.
 */
export function currentRung(scope: Scope | null, country: Country): Rung {
  if (!scope) return country === 'ALL' ? 'programme' : 'country';
  if (scope.unitType === WATERSHED_LEVEL) return WATERSHED_LEVEL;
  if (scope.unitType === PARTNERSHIP_LEVEL) return PARTNERSHIP_LEVEL;
  if (scope.adminLevel === 0) return 'country';
  return scope.adminLevel === 2 ? 'commune' : 'region';
}

/**
 * The levels worth breaking a rung down into, **best first**.
 *
 * The client's own sentence for the country rung is *"its watersheds where data exists, else
 * its regions for roll-up indicators"*, so watersheds lead: they are the programme's own
 * operational geography, they are where the baseline was measured, and where a roll-up has
 * them they are the more meaningful split than an administrative one. `breakdownPlan` takes
 * the first entry the indicator can actually answer for, which is what makes one table serve
 * both families.
 *
 * A commune and a partnership are the bottom of their branches: nothing is below them, and an
 * empty candidate list is how the block gets dropped rather than rendered hollow.
 */
export const RUNG_CHILDREN: Record<Rung, readonly ReportingLevel[]> = {
  programme: [0],
  country: [WATERSHED_LEVEL, 1],
  region: [2],
  commune: [],
  watershed: [PARTNERSHIP_LEVEL],
  partnership: [],
};

export interface BreakdownPlan {
  rung: Rung;
  /** The level the bars and the donut describe. */
  level: ReportingLevel;
  /** §D2 `type=` for the name list that goes with `level`. */
  unitType: UnitType;
  /** `?parent=` for both calls — `null` at the programme and country rungs. */
  parent: string | null;
  /** `indicators.level*` — the i18n key naming the rung, for the section head. */
  labelKey: string;
}

/** `admin` for the three admin levels, the overlay's own type otherwise. */
export function unitTypeFor(level: ReportingLevel): UnitType {
  if (level === WATERSHED_LEVEL) return WATERSHED_LEVEL;
  if (level === PARTNERSHIP_LEVEL) return PARTNERSHIP_LEVEL;
  return 'admin';
}

const LEVEL_LABEL_KEYS: Record<string, string> = {
  0: 'breakdown.byCountry',
  1: 'breakdown.byRegion',
  2: 'breakdown.byCommune',
  [WATERSHED_LEVEL]: 'breakdown.byWatershed',
  [PARTNERSHIP_LEVEL]: 'breakdown.byPartnership',
};

/**
 * What to break the current view down into, or `null` when the honest answer is *nothing*.
 *
 * `null` has three causes and they are all the data's rather than the code's: the reader is
 * at the bottom of a branch, the catalogue has not answered yet (`effectiveLevels` is empty,
 * and "we do not know" must never render as "there is nothing"), or this is a survey
 * estimate under a rung whose children it was never measured at — the region case the client
 * called out, where the map is already showing the honest empty state.
 */
export function breakdownPlan(
  meta: LevelCapableIndicator | null | undefined,
  scope: Scope | null,
  country: Country,
): BreakdownPlan | null {
  if (!meta) return null;
  const available = effectiveLevels(meta);
  if (!available.length) return null;
  const rung = currentRung(scope, country);
  const level = RUNG_CHILDREN[rung].find((candidate) => available.includes(candidate));
  if (level === undefined) return null;
  return {
    rung,
    level,
    unitType: unitTypeFor(level),
    // The country rung filters through `?country=`, which is what the map itself does with
    // it; only a real scope has a unit id to pass as `?parent=`.
    parent: scope && rung !== 'country' ? scope.unitId : null,
    labelKey: LEVEL_LABEL_KEYS[String(level)] ?? 'breakdown.byRegion',
  };
}

export interface BreakdownRow {
  unitId: string;
  /** The unit's name where `/hierarchy/options` knows it, else its id. */
  name: string;
  value: number;
  /** §D3 `sample_n` — present for a survey estimate, null for everything else. */
  sampleN: number | null;
  /** ISO3, where the row's own unit carries one — the country palette's key. */
  iso: string | null;
}

/** One §D3 values row, structurally — so tests need not build a whole `ValuesResponse`. */
export interface BreakdownValue {
  unit_id: string;
  value: number | null;
  sample_n?: number | null;
}

/**
 * The bars, **largest first**, named from the cheap `/hierarchy/options` list.
 *
 * Sorted rather than left in the server's order because the question a breakdown answers is
 * "which of these is biggest", and §D3 orders by unit id. A row §D3 omitted is a unit with no
 * value, and it is left out rather than drawn at zero: a zero bar asserts a measurement of
 * nought where the truth is that nothing was reported.
 */
export function breakdownRows(
  values: readonly BreakdownValue[] | undefined,
  options: readonly UnitOption[] | undefined,
): BreakdownRow[] {
  const named = new Map((options ?? []).map((o) => [o.id, o]));
  const rows: BreakdownRow[] = [];
  for (const row of values ?? []) {
    if (typeof row.value !== 'number' || !Number.isFinite(row.value)) continue;
    const opt = named.get(row.unit_id);
    rows.push({
      unitId: row.unit_id,
      name: opt?.name ?? row.unit_id,
      value: row.value,
      sampleN: row.sample_n ?? null,
      iso: opt?.country ?? null,
    });
  }
  return rows.sort((a, b) => b.value - a.value);
}

/**
 * Whether a donut would be a true statement about these numbers, and which one.
 *
 * A doughnut is a claim that the slices **compose a whole**, and the two indicator families
 * compose in different ways — so this is the one place the distinction is made, and both the
 * panel and its tests read it from here.
 *
 * * `composition` — an **additive** indicator (`aggregation: 'sum'`: producers reached,
 *   hectares, institutions). Its children genuinely sum to the parent — Burkina Faso's four
 *   regions add up to Burkina Faso's 14 702 — so the ring is the real share of each unit in
 *   the total, and the centre is that total.
 * * `share` — a **percentage estimate**. 9.9 % of Burkina Faso's surveyed households apply
 *   manure to their trees; the composition that percentage *is* is 9.9 against the other
 *   90.1, and drawing it is the same gauge §E4's target ring already uses. This is what gives
 *   a baseline variable a donut without inventing one.
 * * `null` — a **mean that is not a percentage** (kg/ha, SPI-3, g/kg). Four countries' mean
 *   yields do not add to a programme yield, and a ring of them would be a pie chart of
 *   averages: the single most common way to lie with a doughnut. The bars still answer the
 *   comparison; the ring is simply refused.
 *
 * The percentage test is the unit string rather than a flag because that is where §C records
 * it (`%`, `% farms`, `% cropland`, `% farmers`), and the value is bounds-checked as well —
 * a "percentage" outside 0–100 is a catalogue error, and 140 % of a ring is a drawing bug.
 */
export type DonutShape =
  | { kind: 'composition'; total: number }
  | { kind: 'share'; value: number }
  | null;

export function donutShape(
  meta: { aggregation: 'sum' | 'mean' | 'none'; unit?: string | null } | null | undefined,
  value: number | null,
  rows: readonly BreakdownRow[],
): DonutShape {
  if (!meta) return null;
  if (meta.aggregation === 'sum') {
    // Two slices is not a composition worth a ring; one is a circle.
    if (rows.length < 2) return null;
    const total = rows.reduce((sum, row) => sum + row.value, 0);
    return total > 0 ? { kind: 'composition', total } : null;
  }
  const pct = (meta.unit ?? '').trim().startsWith('%');
  if (pct && value !== null && Number.isFinite(value) && value >= 0 && value <= 100) {
    return { kind: 'share', value };
  }
  return null;
}

/**
 * How many bars fit before the block stops being readable in a ~250 px column.
 *
 * Eight rows at 18 px is 144 px of chart, which sits under the trend spark without pushing
 * the sheet comparison off the fold. Communes under a region can run past it, so the panel
 * prints how many were left out rather than silently truncating — see `breakdown.more`.
 */
export const MAX_BREAKDOWN_ROWS = 8;

/** §F5.8 row order, for asserting a plan's level without importing the whole ladder. */
export const BREAKDOWN_LEVEL_ORDER = ALL_REPORTING_LEVELS;
